/**
 * Settings -> Compliance Pack template.
 *
 * Per-tenant branding for every artefact in every pack export. Backend stores
 * this in TenantPackTemplate and falls back to Praxara defaults when missing.
 *
 * Edits on this page take effect on the next /api/compliance-pack/export call;
 * existing exported packs (in storage / on auditor share-links) are immutable.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { RoleGateCard } from './admin/AdminPromptVersions';

interface PackTemplate {
  id: string;
  tenantId: string;
  headerText: string;
  footerText: string;
  logoUrl: string | null;
  primaryColorHex: string;
  secondaryColorHex: string;
  accentColorHex: string;
  fontFamily: string;
}

const DEFAULTS = {
  headerText: 'Compliance Pack -- generated by Praxara',
  footerText: 'Confidential -- for the named tenant',
  logoUrl: null as string | null,
  primaryColorHex: '2b4276',
  secondaryColorHex: '0a0e1a',
  accentColorHex: '12b0c5',
  fontFamily: 'Inter',
};

function withHash(c: string): string {
  return c.startsWith('#') ? c : `#${c}`;
}

function withoutHash(c: string): string {
  return c.replace(/^#/, '').toLowerCase();
}

export function SettingsPackTemplatePage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN';

  const tplQ = useQuery({
    queryKey: ['admin', 'pack-template'],
    queryFn: async () => (await api.get<PackTemplate>('/api/admin/pack-template')).data,
    enabled: isAdmin,
    retry: false,
  });

  if (!isAdmin) {
    return <RoleGateCard requiredRole="ADMIN" surface="Compliance Pack branding" />;
  }

  const [form, setForm] = useState<Omit<PackTemplate, 'id' | 'tenantId'>>({
    headerText: DEFAULTS.headerText,
    footerText: DEFAULTS.footerText,
    logoUrl: null,
    primaryColorHex: DEFAULTS.primaryColorHex,
    secondaryColorHex: DEFAULTS.secondaryColorHex,
    accentColorHex: DEFAULTS.accentColorHex,
    fontFamily: DEFAULTS.fontFamily,
  });

  useEffect(() => {
    if (tplQ.data) {
      setForm({
        headerText: tplQ.data.headerText,
        footerText: tplQ.data.footerText,
        logoUrl: tplQ.data.logoUrl,
        primaryColorHex: tplQ.data.primaryColorHex,
        secondaryColorHex: tplQ.data.secondaryColorHex,
        accentColorHex: tplQ.data.accentColorHex,
        fontFamily: tplQ.data.fontFamily,
      });
    }
  }, [tplQ.data]);

  const save = useMutation({
    mutationFn: async () => (await api.patch<PackTemplate>('/api/admin/pack-template', form)).data,
    onSuccess: () => {
      toast.success('Branding saved', 'Takes effect on the next pack export.');
      void qc.invalidateQueries({ queryKey: ['admin', 'pack-template'] });
    },
    onError: (err) => toast.error('Save failed', extractApiError(err).message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  function reset() {
    setForm({
      headerText: DEFAULTS.headerText,
      footerText: DEFAULTS.footerText,
      logoUrl: null,
      primaryColorHex: DEFAULTS.primaryColorHex,
      secondaryColorHex: DEFAULTS.secondaryColorHex,
      accentColorHex: DEFAULTS.accentColorHex,
      fontFamily: DEFAULTS.fontFamily,
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Compliance Pack branding</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Logo, header text, footer text, fonts, and brand colours that appear on every PDF + DOCX
          artefact in your Compliance Pack. Edits take effect on the next export.
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          {/* Form */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Brand text</CardTitle>
                <CardDescription>Shown on the cover band and footer of every artefact.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="headerText">Header text</Label>
                  <Input
                    id="headerText"
                    value={form.headerText}
                    onChange={(e) => setForm({ ...form, headerText: e.target.value })}
                    maxLength={160}
                    placeholder={DEFAULTS.headerText}
                  />
                </div>
                <div>
                  <Label htmlFor="footerText">Footer text</Label>
                  <Input
                    id="footerText"
                    value={form.footerText}
                    onChange={(e) => setForm({ ...form, footerText: e.target.value })}
                    maxLength={200}
                    placeholder={DEFAULTS.footerText}
                  />
                </div>
                <div>
                  <Label htmlFor="fontFamily">Font family (Word-installed)</Label>
                  <select
                    id="fontFamily"
                    value={form.fontFamily}
                    onChange={(e) => setForm({ ...form, fontFamily: e.target.value })}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {['Inter', 'Calibri', 'Times New Roman', 'Arial', 'Helvetica', 'Georgia'].map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Must be installed where the DOCX is opened. Inter is bundled in PDF rendering.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Logo</CardTitle>
                <CardDescription>
                  Public URL surfaced as a wordmark in DOCX and a header band image in PDF. We store the URL
                  only -- no fetch.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="logoUrl">Logo URL</Label>
                  <Input
                    id="logoUrl"
                    type="url"
                    value={form.logoUrl ?? ''}
                    onChange={(e) => setForm({ ...form, logoUrl: e.target.value || null })}
                    placeholder="https://your-cdn.example.com/logo.png"
                  />
                </div>
                {form.logoUrl ? (
                  <div className="flex items-center gap-3 rounded-md border bg-muted/40 p-3 text-xs">
                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="break-all font-mono">{form.logoUrl}</span>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                    No logo configured -- the Praxara wordmark will be used.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Brand colours</CardTitle>
                <CardDescription>Hex values; leading # optional. Applied to PDF cover bands + DOCX heading colours.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-3">
                <ColorField
                  label="Primary"
                  value={form.primaryColorHex}
                  onChange={(v) => setForm({ ...form, primaryColorHex: withoutHash(v) })}
                />
                <ColorField
                  label="Secondary"
                  value={form.secondaryColorHex}
                  onChange={(v) => setForm({ ...form, secondaryColorHex: withoutHash(v) })}
                />
                <ColorField
                  label="Accent"
                  value={form.accentColorHex}
                  onChange={(v) => setForm({ ...form, accentColorHex: withoutHash(v) })}
                />
              </CardContent>
            </Card>

            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={save.isPending}>
                <Save className="mr-1.5 h-3.5 w-3.5" /> {save.isPending ? 'Saving...' : 'Save branding'}
              </Button>
              <Button type="button" variant="outline" onClick={reset}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset to Praxara defaults
              </Button>
            </div>
          </div>

          {/* Live preview */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Cover preview</CardTitle>
                <CardDescription>Approximate look of the artefact cover band. Real artefacts use brand-coloured PDF bands and the configured logo.</CardDescription>
              </CardHeader>
              <CardContent>
                <CoverPreview form={form} />
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={withHash(value)}
          onChange={(e) => onChange(e.target.value)}
          aria-label={`${label} colour picker`}
          className="h-9 w-12 cursor-pointer rounded border border-input bg-background"
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} maxLength={7} className="font-mono" />
      </div>
    </div>
  );
}

function CoverPreview({ form }: { form: Omit<PackTemplate, 'id' | 'tenantId'> }) {
  const primary = withHash(form.primaryColorHex);
  const accent = withHash(form.accentColorHex);
  return (
    <div className="overflow-hidden rounded-md border">
      <div
        className="relative px-5 py-6 text-white"
        style={{
          backgroundColor: primary,
          fontFamily: form.fontFamily,
        }}
      >
        <div className="text-[10px] uppercase tracking-wider opacity-80">{form.headerText}</div>
        <div className="mt-3 text-xl font-semibold leading-tight">EU AI Act -- Annex III Classification</div>
        <div className="mt-1 text-[11px] opacity-70">FULL tier -- 9 artefacts</div>
        <div
          className="mt-4 h-1 rounded-full"
          style={{ background: `linear-gradient(90deg, ${accent} 0%, transparent 100%)` }}
        />
      </div>
      <div
        className="bg-white px-5 py-4 text-[11px] text-ink-700"
        style={{ fontFamily: form.fontFamily }}
      >
        <p>This artefact is generated from your live tenant data on every export. The full pack ships in PDF + DOCX + Markdown formats per artefact, plus a plain-text MANIFEST.</p>
        <p className="mt-2">Generated by Praxara for {`{{ tenantName }}`} -- {new Date().toISOString().slice(0, 10)}.</p>
      </div>
      <div className="border-t bg-muted/40 px-5 py-2 text-[10px] text-muted-foreground">{form.footerText}</div>
    </div>
  );
}
